import { useEffect, useRef } from "react";
import SpatialNavigation from "spatial-navigation-js";
import { useDispatch } from "react-redux";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { animateScrollVideosY } from "./hooks/ScrollUtils";
import { useSelector } from "react-redux";
import { selectApp, setLoading } from "./app/appSlice";

gsap.registerPlugin(ScrollToPlugin);

const useSpatialNavigation = (spatialId, options = {}, videosRef) => {
  const isSNInitialized = useRef(false);
  const dispatch = useDispatch();
  const { lastFocusedElement } = useSelector(selectApp);

  useEffect(() => {
    if (!isSNInitialized.current) {
      SpatialNavigation.init();

      SpatialNavigation.add({
        id: spatialId,
        selector: `#${spatialId} .focusable`,
        enterTo: options.enterTo || "last-focused",
        straightOnly: options.straightOnly ?? true,
        restrict: options.restrict || "self-only",
        leaveFor: options.leaveFor || {},
      });

      SpatialNavigation.makeFocusable();
      isSNInitialized.current = true;
    }

    return () => {
      SpatialNavigation.remove(spatialId);
      isSNInitialized.current = false;
    };

  },[spatialId, options]);

  // ANIMATE SCROLL
  useEffect(() => {
      const handleFocusEvent = (event) => {
        const targetElement = event.detail.target || document.activeElement;

        // Scroll GRID page
        if ((event.detail.direction === "up" || event.detail.direction === "down") && videosRef?.current) {
          if (targetElement && targetElement !== document.body) {
            animateScrollVideosY(targetElement, videosRef);
          }
        }

        // Scroll HOME page
        const focusedElement = event.detail?.sectionId;
        if (focusedElement === spatialId && spatialId === "middlebox") {
          const el = document.querySelector(`#${spatialId} .focusable:focus`);
          if (el) {
            const parentRow = el.closest(".category-row");
            if (parentRow && !parentRow.classList.contains("first-row")) {
              parentRow.scrollIntoView();
            }

            // Scroll slider row
            const slideRow = el.closest(".series-list");
            if ((event.detail.direction === "right" || event.detail.direction === "left") && slideRow) {
              const targetEpisode = el;
              const seriesList = slideRow;
              const seriesListWrapperWidth = document.getElementsByClassName("series-list-wrapper")[0].offsetWidth;
              const episodeWidth = targetEpisode.offsetWidth;
              const episodeOffsetLeft = targetEpisode.offsetLeft;
            
              // Calculate the desired scroll position (center the target episode)
              let x = episodeOffsetLeft - (seriesListWrapperWidth / 2) + (episodeWidth / 2);
            
              const maxScrollLeft = seriesList.scrollWidth - seriesListWrapperWidth;
            
              x = Math.max(0, Math.min(x, maxScrollLeft));
              
              gsap.to(seriesList, {
                x: -x,
                duration: event.repeat ? 0.2 : 0.4,
                ease: "power3.out",
                overwrite: true,
                onStart: () => {
                  SpatialNavigation.disable("middlebox");
                  setTimeout(() => {
                    SpatialNavigation.enable("middlebox");
                  },100);
                },
              });
    
            }
          }
        }        
      };

      document.addEventListener("sn:focused", handleFocusEvent);

      return () => document.removeEventListener("sn:focused", handleFocusEvent);
    },[spatialId, videosRef]);
      
    
    // SCROLL TO LAST ELEMENT ON NAVIGATING BACK PAGE
    useEffect(() => {
      const waitForElementById = (id, timeout = 7000) => {
        return new Promise((resolve) => {
          const intervalTime = 10;
          let elapsedTime = 0;
      
          const interval = setInterval(() => {
            const el = document.getElementById(id);
            if (el) {
              clearInterval(interval);
              resolve(el);
            } else if (elapsedTime >= timeout) {
              clearInterval(interval);
              resolve(null);
            }
            elapsedTime += intervalTime;
          }, intervalTime);
        });
      };

      // 👇 Auto-focus last focused element if exists
      if (lastFocusedElement) {
        // document.activeElement?.blur();
        const container = document.getElementById(spatialId);
        console.log("container", container);
        waitForElementById(lastFocusedElement.id, 7000).then((el) => {
          if (container && el) {
            el.focus();
      
            const parentRow = el.closest(".category-row");
            if (container.id === "middlebox") {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  parentRow?.scrollIntoView();
                  dispatch(setLoading(false));
                });
              });
            } else {
              if (videosRef?.current) {
                animateScrollVideosY(el, videosRef);
                dispatch(setLoading(false));
              }
            }
          }
        });
      } else if (spatialId === "upperbox") {
        SpatialNavigation.focus("upperbox");
      }
     
  // eslint-disable-next-line
  }, [dispatch, spatialId, videosRef]);

  return null;
};

export default useSpatialNavigation;
